import * as React from "react";
import { toast, ToastOptions } from "react-toastify";
import { ClientService } from "@app/client/clientService";
import { Page, PageHeader, PageInner, PageSection, LoadingSpinner, Breadcrumbs, PageHeaderRight, BreadcrumbTemplateEnum } from "@app/shared";
import { ColumnConfig, Waffles, wafflesKeyColumnName, SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { AddJobQueueMapFlyout, TestJobQueueMappingsFlyout, JobQueueService } from "@app/jobs";
import { JobQueueMapListItem, JobQueueMapSummary } from "./jobQueueMapSummary";
import { QESButton, QESMultiSelect, QESTextInput } from "@app/shared/qes-standards";
import { useHistory } from "react-router-dom";
import { JobType, } from "./job";
import { MultiselectItem, MultiselectOnChangeResponse } from "../shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "../shared/qes-standards/forms/Checkbox";
import { Client } from "../client/client";
import { JobQueue } from "./job-queues/jobQueue";
import { Flyout, FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import FlyoutContextProvider, { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { JSX } from "react";
import { NestingParentDropdownItem, SingleSelectNestedDropdown } from '@app/shared/checkbox-dropdown';
import { GeographyService } from "@app/geography";
import { CountyLite } from "@app/geography/county";

interface JobQueueMapTableData {
  [wafflesKeyColumnName]: string;
  clientId?: number;
  clientName: string;
  jobQueueId?: number;
  jobQueueMapId: JSX.Element;
  jobQueueName: string;
  jobTypeId?: number;
  jobTypeName: string;
  projectId?: number;
  projectName: string;
  countySSA: string;
}

interface JobQueueMapFilter {
  jobTypes: JobType[];
  jobQueue: string;
  client: string;
  project: string;
  countySSA: string;
}

const jobQueueMapsColumnConfig: ColumnConfig = {
  clientId: {
    displayText: "Client ID",
    testId: "clientId",
  },
  clientName: {
    displayText: "Client",
    testId: "clientName",
  },
  countySSA: {
    displayText: "County SSA",
    testId: "countySSA",
  },
  jobQueueId: {
    displayText: "Job Queue ID",
    testId: "jobQueueId",
  },
  jobQueueMapId: {
    displayText: "Id",
    testId: "jobQueueMapId",
  },
  jobQueueName: {
    displayText: "Job Queue",
    testId: "jobQueueName",
  },
  jobStatus: {
    displayText: "Job Status",
    testId: "jobStatus",
  },
  jobStatusDate: {
    displayText: "Job Status Date",
    sizeContentToFit: true,
    testId: "jobStatusDate",
  },
  jobTypeId: {
    displayText: "Job Type ID",
    testId: "jobTypeId",
  },
  jobTypeName: {
    displayText: "Job Type",
    testId: "jobTypeName",
  },
  projectId: {
    displayText: "Project ID",
    testId: "projectId",
  },
  projectName: {
    displayText: "Project",
    testId: "projectName",
  },
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<JobQueueMapTableData>,
}

interface JobTypeMultiselectItem extends MultiselectItem {
  value: JobType
}

type JobQueueMapViews = "Job Queue Map Info";

const WrappedJobQueueMap: React.FC<React.PropsWithChildren<unknown>> = () => {
  const clientService = new ClientService();
  const jobQueueService = new JobQueueService();
  const geographyService = new GeographyService();
  const urlParams = new URLSearchParams(window.location.search);
  const history = useHistory();
  const flyoutButtonRefs = React.useRef<Array<React.RefObject<HTMLButtonElement>>>([]);
  const deleteButtonRef = React.useRef<HTMLButtonElement>(null);

  const [activeView, setActiveView] = React.useState<JobQueueMapViews>("Job Queue Map Info");
  const [allClients, setAllClients] = React.useState<Client[]>(null);
  const [allQueues, setAllQueues] = React.useState<JobQueue[]>(null);
  const [client, setClient] = React.useState(urlParams.get("client") ?? "");
  const [currentFlyoutButton, setCurrentFlyoutButton] = React.useState<number>();
  const [isFiltered, setIsFiltered] = React.useState(false);
  const [jobDetailsLoading, setJobDetailsLoading] = React.useState(false);
  const [jobQueue, setJobQueue] = React.useState(urlParams.get("queue") ?? "");
  const [jobQueueMapFilter, setJobQueueMapFilter] = React.useState<JobQueueMapFilter>({
    client: urlParams.get("client"),
    countySSA: urlParams.get("countySSA"),
    jobQueue: urlParams.get("queue"),
    jobTypes: JSON.parse(urlParams.get("types")),
    project: urlParams.get("proj"),
  });
  const [jobQueueMapItem, setJobQueueMapItem] = React.useState<JobQueueMapListItem>(null);
  const [jobQueueMapSummary, setJobQueueMapSummary] = React.useState<JobQueueMapSummary>(null);
  const [jobTypes] = React.useState<{ [key: string]: JobType }>(() => {
    const jobTypes: { [key: string]: JobType } = {};
    Object.entries(JobType).forEach(v => {
      jobTypes[v[0]] = v[1];
    });
    return jobTypes;
  })
  const [loading, setLoading] = React.useState(true);
  const [modalWarningVisible, setModalWarningVisible] = React.useState<boolean>(false);
  const [numDispRecLowerBound, setNumDispRecLowerBound] = React.useState(0);
  const [numDispRecUpperBound, setNumDispRecUpperBound] = React.useState(0);
  const [pageSettings, setPageSettings] = React.useState<PageSettings>(!!urlParams.get("pageSet") ? JSON.parse(urlParams.get("pageSet")) :
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      },
    });
  const [project, setProject] = React.useState(urlParams.get("proj") ?? "");
  const [counties, setCounties] = React.useState<CountyLite[]>([]);
  const [countySSA, setCountySSA] = React.useState(urlParams.get("countySSA") ?? "");
  const [selectedCounty, setSelectedCounty] = React.useState(null);
  const [selectedJobQueueMaps, setSelectedJobQueueMaps] = React.useState<string[]>([]);
  const [selectedJobTypeItems, setSelectedJobTypeItems] = React.useState<string[]>(JSON.parse(urlParams.get("types")) ?? Object.entries(JobType).map(e => e[0]));
  const jobDetailFlyoutContext = React.useContext(FlyoutContext);
  const [take] = React.useState(10);
  const [totalRecords, setTotalRecords] = React.useState(0);

  React.useEffect(() => {
    if (!jobDetailFlyoutContext.isFlyoutVisible) {
      setCurrentFlyoutButton(null);
    }
  }, [jobDetailFlyoutContext.isFlyoutVisible])

  const jobQueueMapTableData: JobQueueMapTableData[] = React.useMemo<JobQueueMapTableData[]>(() => {

    let data: JobQueueMapTableData[] = [];

    if (jobQueueMapSummary) {
      /* eslint-disable sort-keys */
      data = jobQueueMapSummary.jobQueueMaps.map((jobQueueMap: JobQueueMapListItem, index: number) => ({
        key: jobQueueMap.jobQueueMapId.toString(),
        jobQueueMapId: (
          <QESButton
            qesTestId="jobqueueMap-details-content_cancel"
            qesType="secondary"
            onClick={() => handleJobQueueMapClick(jobQueueMap, index)}
            qesRef={flyoutButtonRefs.current[index]}
          >
            {jobQueueMap.jobQueueMapId}
          </QESButton>),
        jobTypeId: jobQueueMap.jobTypeId,
        jobTypeName: jobQueueMap.jobTypeName,
        jobQueueId: jobQueueMap.jobQueueId,
        jobQueueName: (!!jobQueueMap.jobQueueName ? jobQueueMap.jobQueueName : "--"),
        clientId: jobQueueMap.clientId,
        clientName: jobQueueMap.clientName,
        projectId: jobQueueMap.projectId,
        projectName: jobQueueMap.projectName,
        countySSA: jobQueueMap.countySSA,
      }));
      /* eslint-enable sort-keys */
    }
    setLoading(false);
    return data;
  }, [jobQueueMapSummary, currentFlyoutButton]);

  const getCountyDropdownItem = (county: CountyLite) => {
    return ({
      display: county.state + " - " + county.name,
      key: county.countySSA,
      name: county.name,
    });
  }

  const countyNestedDropdownItems: NestingParentDropdownItem[] = React.useMemo<NestingParentDropdownItem[]>(() => {
    const states: string[] = [];
    const stateCounties: { [id: string]: CountyLite[] } = {};
    for (const county of counties) {
      if (!states.includes(county.state)) {
        states.push(county.state);
        stateCounties[county.state] = [];
      }
      stateCounties[county.state].push(county);
    }
    return states.map((state) => ({
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children: stateCounties[state].sort((a, b) => a.name.localeCompare(b.name)).map((c) => (getCountyDropdownItem(c))),
      key: state,
      name: state
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [counties]);

  const jobTypeMultiSelectItems: { [key: string]: MultiselectItem } = React.useMemo<{ [key: string]: MultiselectItem }>(() => {
    const jobTypeItems: { [key: string]: MultiselectItem } = {};

    Object.entries(JobType).forEach((enumKey) => {
      const currentState =
        (!!selectedJobTypeItems && Array.isArray(selectedJobTypeItems))
          ? selectedJobTypeItems.includes(enumKey[0]) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked
          : CheckboxStateEnum.Checked;
      jobTypeItems[enumKey[0]] = { currentState, displayValue: enumKey[1], value: enumKey[0] } as JobTypeMultiselectItem;
    });
    return jobTypeItems;
  }, [selectedJobTypeItems]);


  const handleAddJobQueueMapSubmit = async (jobQueueId: number, jobType?: JobType, clientId?: number, projectId?: number, countySSA?: string) => {
    try {
      await jobQueueService.postJobQueueMap(jobQueueId, jobType, clientId, projectId, countySSA);
      handleSearch();
    }
    catch {
      const toastOptions: ToastOptions = {
        closeOnClick: true,
        containerId: "z-index-9999",
      };

      toast.error('Create Job Queue Map Failed', toastOptions);
    }
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take }),
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: (jobQueueMapSummary?.totalJobQueueMaps ?? 0),
  }

  const setClients = (clients: Client[]) => {
    setAllClients(clients);
  }

  const setQueues = (jobQueues: JobQueue[]) => {
    setAllQueues(jobQueues);
  }

  React.useEffect(() => {
    refreshData();
  }, [pageSettings]);

  React.useEffect(() => {
    clientService.getAllClients().then(setClients);
    jobQueueService.getAvailableJobQueues().then(setQueues);
    geographyService.getCountiesLite().then(setCounties);
    refreshData();
  }, []);

  React.useEffect(() => {
    if (!countySSA || !counties)
      return;

    if (!!selectedCounty && selectedCounty.key === countySSA)
      return;

    const county = counties.find((c) => c.countySSA === countySSA);
    if (!county)
      return;

    setSelectedCounty(getCountyDropdownItem(county));
  }, [countySSA, counties]);

  const jobQueueMapInfoTab = (
    <>
      {!!jobQueueMapItem ? (
        <>
          <dl className="d-table mb-0">
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Job Queue Map Id: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.jobQueueMapId}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Job Type Id: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.jobTypeId}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Job Type: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.jobTypeName}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Client Id: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.clientId}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Client Name: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.clientName}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Project Id: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.projectId}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Project: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.projectName}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">CountySSA: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.countySSA}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Job Queue Id: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.jobQueueId}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell label-s">Job Queue: </dt>
              <dd className="d-table-cell pl-2">{jobQueueMapItem.jobQueueName}</dd>
            </div>
          </dl>
        </>) : null
      }
    </>);

  const jobQueueMapDetailViews: { [key: string]: JSX.Element } = React.useMemo(() => {
    const views: { [key: string]: JSX.Element } = {
      "Job Queue Map Info": (
        <PageSection>
          {jobQueueMapInfoTab}
        </PageSection>
      )
    };

    return views;
  }, [jobQueueMapItem]);

  const refreshData = () => {
    setLoading(true);
    const filteredTypes =
      !!jobQueueMapFilter.jobTypes && Array.isArray(jobQueueMapFilter.jobTypes)
        ? (jobQueueMapFilter.jobTypes as Array<JobType>).filter((t) => Object.entries(JobType).map(e => e[0]).includes(t))
        : Object.keys(JobType);
    jobQueueService.getJobQueueMapSummary(pageSettings.skip, take, filteredTypes, pageSettings.sortDetails.sortedColumn, pageSettings.sortDetails.sortDirection, jobQueueMapFilter.jobQueue, jobQueueMapFilter.client, jobQueueMapFilter.project, jobQueueMapFilter.countySSA).then((summary) => {

      setJobQueueMapSummary(summary);
      setTotalRecords(summary.totalJobQueueMaps);

      setNumDispRecLowerBound(pageSettings.skip + 1);
      setNumDispRecUpperBound(summary.jobQueueMaps.length + pageSettings.skip);

      setIsFiltered(!!((jobQueueMapFilter.jobTypes && jobQueueMapFilter.jobTypes.length < Object.entries(JobType).length) || jobQueueMapFilter.jobQueue || jobQueueMapFilter.client || jobQueueMapFilter.project || jobQueueMapFilter.countySSA));

      const newFlyoutRefs = [];
      for (let i = 0; i < summary.totalJobQueueMaps; i += 1) {
        newFlyoutRefs[i] = React.createRef<HTMLButtonElement>();
      }
      flyoutButtonRefs.current = newFlyoutRefs;

      const searchParams = new URLSearchParams(window.location.search);
      if (pageSettings.skip > 0 || pageSettings.sortDetails.sortDirection !== SortDirections.None) {
        searchParams.set("pageSet", JSON.stringify(pageSettings));
      } else {
        searchParams.delete("pageSet");
      }
      if (!!jobQueueMapFilter.jobTypes && jobQueueMapFilter.jobTypes.length < Object.entries(JobType).length) {
        searchParams.set("types", JSON.stringify(jobQueueMapFilter.jobTypes));
      } else {
        searchParams.delete("types");
      }
      if (!!jobQueueMapFilter.jobQueue) {
        searchParams.set("queue", jobQueueMapFilter.jobQueue);
      } else {
        searchParams.delete("queue");
      }
      if (!!jobQueueMapFilter.client) {
        searchParams.set("client", jobQueueMapFilter.client);
      } else {
        searchParams.delete("client");
      }
      if (!!jobQueueMapFilter.project) {
        searchParams.set("proj", jobQueueMapFilter.project);
      } else {
        searchParams.delete("proj");
      }
      if (!!jobQueueMapFilter.countySSA) {
        searchParams.set("countySSA", jobQueueMapFilter.countySSA);
      } else {
        searchParams.delete("countySSA");
      }
      history.replace(history.location.pathname + "?" + searchParams.toString());
    }, () => {
      setJobQueueMapSummary(null);
    });
  }

  const clearFilter = () => {
    setSelectedJobTypeItems(Object.entries(JobType).map(e => e[0]));
    setJobQueue("");
    setClient("");
    setProject("");
    setCountySSA("");

    setJobQueueMapFilter({
      client: "",
      countySSA: "",
      jobQueue: "",
      jobTypes: JSON.parse("{}"),
      project: "",
    });

    setIsFiltered(false);

    setPageSettings({
      ...pageSettings,
      skip: 0
    });
  }

  const handleCheckboxChange = ((selectedKeys: string[]) => {
    setSelectedJobQueueMaps(selectedKeys);
  });

  const handleDeleteMapsClick = () => {
    setModalWarningVisible(true);
  }

  const handleJobQueueMapClick = async (jobQueueMap: JobQueueMapListItem, buttonIndex: number) => {
    setJobDetailsLoading(true);
    setActiveView("Job Queue Map Info");
    setCurrentFlyoutButton(buttonIndex);
    jobDetailFlyoutContext.setIsFlyoutVisible(true);
    setJobQueueMapItem(jobQueueMap);
    setJobDetailsLoading(false);
  }

  const handleSearch = () => {
    const items: Array<JobTypeMultiselectItem> = [];
    Object.values(jobTypeMultiSelectItems).forEach((v: JobTypeMultiselectItem) => items.push(v));

    setJobQueueMapFilter({
      client: client,
      countySSA: countySSA,
      jobQueue: jobQueue,
      jobTypes: items.filter((i: JobTypeMultiselectItem) => selectedJobTypeItems.includes(i.value)).map((i) => i.value),
      project: project,
    });

    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleSort = (data: JobQueueMapTableData[], newSortedColumn: keyof JobQueueMapTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const onClearAllJobTypes = () => setSelectedJobTypeItems([]);

  const onDeleteConfirm = async () => {
    const toastOptions: ToastOptions = {
      closeOnClick: true,
      containerId: "z-index-9999",
    };

    try {
      await jobQueueService.deleteJobQueueMaps(selectedJobQueueMaps);
      setSelectedJobQueueMaps([]);
      handleSearch();
      toast.success('Job Queue Map(s) Deleted', toastOptions);
    }
    catch {
      toast.error('Delete Job Queue Map Failed', toastOptions);
    }
    setModalWarningVisible(false);
  }

  const onInvertJobTypes = () => {
    const selectedItems: string[] = [];
    const selectedJobTypes: JobType[] = [];
    Object.keys(jobTypeMultiSelectItems).forEach((key) => {
      if (!selectedJobTypeItems.includes(key)) {
        selectedItems.push(key);
        selectedJobTypes.push(key as JobType);
      }
    });
    setSelectedJobTypeItems(selectedItems);
    setJobQueueMapFilter({
      ...jobQueueMapFilter,
      jobTypes: selectedJobTypes
    });
  }

  const onSelectAllJobTypes = () => {
    const selectedItems: string[] = [];
    const selectedJobTypes: JobType[] = [];
    Object.keys(jobTypeMultiSelectItems).forEach((key) => {
      selectedItems.push(key);
      selectedJobTypes.push(key as JobType);
    });
    setSelectedJobTypeItems(selectedItems);
    setJobQueueMapFilter({
      ...jobQueueMapFilter,
      jobTypes: selectedJobTypes
    });
  }

  const updateSelectedJobTypes = (response: MultiselectOnChangeResponse) => {
    // take the options as reported and map them into a new collection of string keys for selected items
    const selectedItems: string[] = [];
    const selectedJobTypes: JobType[] = [];
    Object.keys(response.options).filter((key) => response.options[key].currentState === 3).forEach((key) => {
      selectedItems.push((response.options[key] as JobTypeMultiselectItem).value);
      selectedJobTypes.push((response.options[key] as JobTypeMultiselectItem).value);
    });
    setSelectedJobTypeItems(selectedItems);
    setJobQueueMapFilter({
      ...jobQueueMapFilter,
      jobTypes: selectedJobTypes
    });
  }

  const bulkActionBar = (
    <span>
      <QESButton
        disabled={selectedJobQueueMaps.length === 0}
        onClick={handleDeleteMapsClick}
        qesTestId="job-queue-map__delete-maps"
        qesType="secondary"
        qesRef={deleteButtonRef}
      >
        <i className="fa fa-trash" /> Delete Maps
      </QESButton>
    </span>
  );

  return (
    <Page documentTitle="Job Queue Map - Operations">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Job Queue Map"]} />
      <PageInner>
        <PageHeader title="Job Queue Map Listing">
          <PageHeaderRight>
            <FlyoutLink
              content={{
                body: (
                  <AddJobQueueMapFlyout
                    clients={allClients}
                    jobQueues={allQueues}
                    jobTypes={jobTypes}
                    countyItems={countyNestedDropdownItems}
                    onSubmit={handleAddJobQueueMapSubmit}
                  />
                ),
                title: "Add Job Queue Map Entry",
              }}
              testId="job-queue-map-filters__add"
              classNames="qes-button-secondary"
            >
              <i className="fa fa-plus" />&nbsp;Add New Mapping
            </FlyoutLink>
            <FlyoutLink
              content={{
                body: (
                  <TestJobQueueMappingsFlyout
                    clients={allClients}
                    jobTypes={jobTypes}
                    countyItems={countyNestedDropdownItems}
                  />
                ),
                title: "Test Job Queue Mapping",
              }}
              testId="job-queue-map-filters__test"
              classNames="qes-button-secondary"
            >
              <i className="fa fa-check" />&nbsp;Test Mappings
            </FlyoutLink>
          </PageHeaderRight>
        </PageHeader>
        <PageSection>
          <div className="job-queue-map-filters mb-3 mt-3 col">
            <div className="row">
              <div className="mr-3 col-6">
                <QESMultiSelect
                  label="Job Types"
                  onChange={updateSelectedJobTypes}
                  options={jobTypeMultiSelectItems}
                  testId="job-queue-map-job-types"
                />
                <QESButton
                  qesTestId="job-queue_map_filters__defautuserroles-SelectAll"
                  qesType="secondary"
                  onClick={() => onSelectAllJobTypes()}>
                  Select All</QESButton>
                <QESButton
                  qesTestId="job-queue-map-filters__defautuserroles-ClearAll"
                  qesType="secondary"
                  onClick={() => onClearAllJobTypes()}>
                  Clear All</QESButton>
                <QESButton
                  qesTestId="job-queue-map-filters__defautuserroles-Invert"
                  qesType="secondary"
                  onClick={() => onInvertJobTypes()}>
                  Invert Selection</QESButton>
              </div>
              <div className="col">
                <QESTextInput label="Job Queue" testId="job-queue-map-filters__job-queue" value={jobQueue} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setJobQueue(e.target.value)} />
                <QESTextInput label="Client" testId="job-queue-map-filters__client" value={client} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setClient(e.target.value)} />
                <QESTextInput label="Project" testId="job-queue-map-filters__project" value={project} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setProject(e.target.value)} />
                <SingleSelectNestedDropdown
                  containerClassName="col p-0"
                  items={countyNestedDropdownItems}
                  label="County"
                  onSelectionChanged={(item) => {
                    setSelectedCounty(item);
                    if (!item) {
                      setCountySSA("");
                    } else {
                      setCountySSA(item.key);
                    }
                  }}
                  selectedItem={selectedCounty}
                  testId="job-queue-map-filters__county"
                />
              </div>
            </div>
            <div className="row mb-3">
              <QESButton qesType="primary" qesTestId="job-queue-map-filters__search-button" onClick={handleSearch} disabled={loading}>Search</QESButton>
              <QESButton qesType="secondary" qesTestId="job-queue-map-filters__clear-filters" onClick={clearFilter} disabled={!isFiltered || loading}>Clear</QESButton>
            </div>
          </div>
          <div className="col">
            <p>Displaying {totalRecords > 0 ? `${numDispRecLowerBound}-${numDispRecUpperBound} of ${totalRecords}` : "0"} job queue maps {isFiltered ? "(filtered)" : ""}</p>
          </div>
          <Waffles<JobQueueMapTableData>
            bulkActionBar={bulkActionBar}
            columns={jobQueueMapsColumnConfig}
            customSort={handleSort}
            data={jobQueueMapTableData}
            enablePagination={true}
            handleCheckboxChange={handleCheckboxChange}
            loading={loading}
            passThroughSorting={true}
            paginationProps={paginationProps}
            selectedKeys={selectedJobQueueMaps ?? []}
            showCheckboxes={true}
            testId="jobs-queue-map__waffles-table"
          />
          <Modal
            isVisible={modalWarningVisible}
            closeModal={() => setModalWarningVisible(false)}
            content={{
              body: <>{`You are about to delete ${selectedJobQueueMaps.length} job queue map entries.`}<br />Continue with delete?</>,
              cancelButtonText: "Cancel",
              confirmButtonAction: onDeleteConfirm,
              confirmButtonText: "Delete",
              confirmButtonType: "primary-danger",
              title: "Warning",
            }}
            hideBodyScroll={true}
            triggerElement={deleteButtonRef}
          />
          <Flyout
            content={{
              body: jobDetailsLoading ? (<LoadingSpinner />) : (
                <>
                  <div className="tabs-style-2">
                    <ul className="nav nav-tabs">
                      {Object.keys(jobQueueMapDetailViews).map((key) => (
                        <li className="nav-item" key={key}>
                          <a className={`nav-link${activeView === key ? " active" : ""}`} role="tab" onClick={() => setActiveView(key as JobQueueMapViews)}>{key}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="row mt-3 ml10 mr10">
                    {jobQueueMapDetailViews[activeView]}
                  </div>
                </>
              ),
              title: "Job Details",
            }}
            size="20"
            triggerElement={flyoutButtonRefs.current[currentFlyoutButton]}
          />
        </PageSection>
      </PageInner>
    </Page>
  );
}

export const JobQueueMap: React.FC<unknown> = () => {
  return (
    <FlyoutContextProvider>
      <WrappedJobQueueMap />
    </FlyoutContextProvider>
  );
}
